import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiChevronUp, FiUser, FiSearch } from "react-icons/fi";

import TeamBadge from "../TeamBadge";
import useDebounce from "../../hooks/useDebounce";

const ClubbieSelect = ({
  placeholder,
  options = [],
  value,
  onChange,
  onSearch,
  isLoading,
  error,
  getOptionLabel,
  getOptionValue,
  getOptionImage,
  isSearchable = false,
  debounceDelay = 300,
  disabled = false,
  onLoadMore,
  hasMore = false,
  noOptionsMessage = () => "No options available",
  dropdownMinWidth,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const selectRef = useRef(null);
  const searchInputRef = useRef(null);
  const debouncedSearchTerm = useDebounce(searchTerm, debounceDelay);
  const previousSearchTerm = useRef("");

  const updatePosition = () => {
    if (!selectRef.current) return;
    const rect = selectRef.current.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, dropdownMinWidth || 0) });
  };

  useEffect(() => {
    setSearchTerm("");
  }, [value]);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      if (isSearchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  }, [isOpen, isSearchable]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (
      isSearchable &&
      onSearch &&
      debouncedSearchTerm.trim() &&
      debouncedSearchTerm !== previousSearchTerm.current
    ) {
      onSearch(debouncedSearchTerm);
      previousSearchTerm.current = debouncedSearchTerm;
    }
  }, [debouncedSearchTerm, isSearchable, onSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target) &&
        !event.target.closest("[data-clubbie-select-dropdown]")
      ) {
        setIsOpen(false);
        setSearchTerm("");
        previousSearchTerm.current = "";
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOptionClick = (option) => {
    onChange(option);
    setSearchTerm("");
    previousSearchTerm.current = "";
    setIsOpen(false);
  };

  const handleToggleDropdown = () => {
    if (disabled) return;
    if (isOpen) {
      setSearchTerm("");
      previousSearchTerm.current = "";
    }
    setIsOpen(!isOpen);
  };

  const safeGetOptionLabel = (option) => {
    if (!option) return "";
    if (typeof getOptionLabel === "function") return getOptionLabel(option) || "";
    return option?.label || option?.name || option?.title || "";
  };

  const safeGetOptionValue = (option) => {
    if (!option) return "";
    if (typeof getOptionValue === "function") return getOptionValue(option) || "";
    return option?.value || option?.id || option?._id || "";
  };

  const safeGetOptionImage = (option) => {
    if (!option || typeof getOptionImage !== "function") return null;
    return getOptionImage(option) || null;
  };

  const filteredOptions =
    !isSearchable || !Array.isArray(options)
      ? options
      : options.filter((option) =>
          safeGetOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
        );

  const selectedImage = value ? safeGetOptionImage(value) : null;
  const selectedLabel = value ? safeGetOptionLabel(value) : null;

  const dropdown = isOpen ? (
    <div
      data-clubbie-select-dropdown
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        zIndex: 999999,
      }}
      className="bg-app-bg border border-[#192634] rounded-[20px] shadow-lg overflow-hidden"
    >
      {isSearchable && (
        <div className="flex items-center gap-2 px-3 border-b border-[#192634]">
          <FiSearch className="w-4 h-4 text-[#7F8585] shrink-0" />
          <input
            ref={searchInputRef}
            type="text"
            className="flex-1 h-10 bg-transparent text-[#FEFCEB] text-sm outline-none placeholder-[#7F8585]"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div className="max-h-[200px] overflow-y-auto">
        {Array.isArray(filteredOptions) && filteredOptions.length > 0 ? (
          <>
            {filteredOptions.map((option, index) => {
              const optImg = safeGetOptionImage(option);
              const isSelected =
                value && safeGetOptionValue(value) === safeGetOptionValue(option);
              return (
                <div
                  key={safeGetOptionValue(option) || index}
                  className={`flex items-center gap-3 px-3 py-3 cursor-pointer transition-colors border-b border-[#192634] last:border-b-0 ${
                    isSelected
                      ? "bg-[#082441] text-[#00E7FF]"
                      : "text-[#FEFCEB] hover:bg-app-hover"
                  }`}
                  onClick={() => handleOptionClick(option)}
                >
                  {getOptionImage && (
                    <div className={`w-5 h-5 rounded-full border border-[#192634] overflow-hidden shrink-0 flex items-center justify-center bg-[#082441]`}>
                      {optImg ? (
                        <img src={optImg} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                      ) : (
                        <TeamBadge size="w-4 h-4"/>
                      )}
                    </div>
                  )}
                  <span className="text-sm font-medium">{safeGetOptionLabel(option)}</span>
                </div>
              );
            })}
            {hasMore && (
              <div
                className="px-3 py-3 text-[#00E7FF] text-sm text-center cursor-pointer border-t border-[#192634] hover:bg-[#082441]"
                onClick={!isLoading ? onLoadMore : undefined}
              >
                {isLoading ? "Loading..." : "Load more..."}
              </div>
            )}
          </>
        ) : (
          <div className="px-3 py-3 text-[#7F8585] text-sm">
            {isLoading ? "Loading..." : noOptionsMessage()}
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="relative w-full" ref={selectRef}>
      {/* Trigger */}
      <div
        className={`flex items-center justify-between h-11 px-4 bg-[#021529] border rounded-full cursor-pointer transition-colors ${
          isOpen ? "border-[#00E7FF]" : "border-transparent"
        } ${error ? "border-app-error" : ""} ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
        onClick={handleToggleDropdown}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {getOptionImage && (
            <div className="w-5 h-5 rounded-full border border-[#192634] overflow-hidden shrink-0 flex items-center justify-center bg-[#082441]">
              {selectedImage ? (
                <img src={selectedImage} alt="" className="w-full h-full object-cover" />
              ) : (
                        <TeamBadge size="w-4 h-4"/>
              )}
            </div>
          )}
          <span className={`text-[15px] truncate ${selectedLabel ? "text-[#FEFCEB]" : "text-[#7F8585]"}`}>
            {selectedLabel || placeholder}
          </span>
        </div>
        <div className="shrink-0 ml-2">
          {isLoading ? (
            <span className="text-[13px] text-[#7F8585]">Loading...</span>
          ) : isOpen ? (
            <FiChevronUp className="w-4 h-4 text-[#FEFCEB]" />
          ) : (
            <FiChevronDown className="w-4 h-4 text-[#FEFCEB]" />
          )}
        </div>
      </div>

      {error && <div className="mt-1 text-app-error text-[13px]">{error}</div>}

      {createPortal(dropdown, document.body)}
    </div>
  );
};

ClubbieSelect.propTypes = {
  placeholder: PropTypes.string,
  options: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  getOptionLabel: PropTypes.func.isRequired,
  getOptionValue: PropTypes.func.isRequired,
  getOptionImage: PropTypes.func,
  isSearchable: PropTypes.bool,
  debounceDelay: PropTypes.number,
  disabled: PropTypes.bool,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  noOptionsMessage: PropTypes.func,
  dropdownMinWidth: PropTypes.number,
};

export default ClubbieSelect;
